import * as StateLib from "../Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumFeaturesList, kConnectorRefreshDisabled, kTimeList } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { GetChartMetadata } from "./Promises";

export function getHoverTipContent(hoverCode, position = "", extraContent = "") {
  let tooltip = hoverCode;
  if (Globals._stringMgr) tooltip = Globals._stringMgr.getHoverTip(hoverCode);

  let tooltipContent = { message: tooltip };

  if (tooltip.includes("^")) {
    let tooltipParts = tooltip.split("^");
    tooltipContent.title = tooltipParts[0];
    tooltipContent.message = tooltipParts[1];
    //, maxWidth: 200, wordWrap: "break-word"
  }

  if (extraContent) tooltipContent.message += ". " + extraContent;

  if (position?.length > 0) tooltipContent.position = position;
  return tooltipContent;
}

export const createSelectOptions = (
  stringValues,
  setStateVariableFunction,
  dataTestPrefix = undefined,
  localize = false
) => {
  return stringValues.map(v => ({
    label: localize ? appstr(v) : v,
    value: v,
    dataTestId: dataTestPrefix ? `${dataTestPrefix}-${v}` : undefined,
    clickHandler: () => {
      setStateVariableFunction(v);
    }
  }));
};

export const buildTimeOptions = (change, showLocalTime = false, localize = false) => {
  let options = [];
  var offset = -1 * Math.floor(new Date().getTimezoneOffset() / 60);
  let wsMgr = StateLib.GetWSMgr();
  let timeFormat = wsMgr.fOrgInfo.GetTimeFormat();

  for (var anHour = 0; anHour < 24; anHour++) {
    let option = {};
    var offsetTime =
      (anHour + offset > 23
        ? anHour + offset
        : anHour + offset < 0
          ? 24 + anHour + offset
          : anHour + offset) % 24;

    let offsetLabel = kTimeList[offsetTime].split("(")[1].slice(0, -1);
    if (showLocalTime) {
      let localTag = localize ? appstr(offsetLabel) : offsetLabel;

      offsetLabel = `${anHour} (${localTag} ${appstr("Local (time)")})`;
    }

    var gmtTime = -1 * offset + offsetTime;
    var gmtoffsetTime = (gmtTime > 23 ? gmtTime : gmtTime < 0 ? 24 + gmtTime : gmtTime) % 24;
    if (timeFormat === 12) {
      option.label = offsetLabel;
      option.sorting = offsetTime;
    } else {
      option.label = offsetTime;
      option.sorting = offsetTime;
    }
    option.value = gmtoffsetTime;
    option.clickHandler = change;
    options.push(option);
  }
  options.sort((a, b) => (a.sorting > b.sorting ? 1 : -1));

  return options;
};

export const addOrRemove = (list, element) => {
  const index = list.indexOf(element);

  if (index === -1) {
    return [...list, element];
  }

  return list.filter(e => e !== element);
};

export const setObject = (object, value) => {
  return Object.fromEntries(Object.keys(object).map(key => [key, value]));
};

export const handleCopyToClipboard = async textToCopy => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error("Failed to copy: ", err);
      fallbackCopyToClipboard(textToCopy);
    }
  } else {
    fallbackCopyToClipboard(textToCopy);
  }
};

const fallbackCopyToClipboard = text => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in MS Edge.
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    const successful = document.execCommand("copy");
    if (!successful) {
      console.error("Fallback: Oops, unable to copy");
    }
  } catch (err) {
    console.error("Fallback: Oops, unable to copy", err);
  }

  document.body.removeChild(textarea);
};

const IsConnectorRefreshable = connector => {
  var isRefreshable = true;
  if (connector == null) return false;

  var source = connector["source"];

  if (kConnectorRefreshDisabled.includes(source)) return false;

  switch (source) {
    case "UKG Ready":
      isRefreshable = !(connector["ukgDetailed"] === true);
      break;
    case "Thrive TRM":
      isRefreshable = false;
      break;
  }

  return isRefreshable;
};

export const IsRefreshable = curChart => {
  var canManualRefresh = true;
  var curImportInfo = curChart;
  if (curImportInfo == null) return false;

  var sectionKeys = ["import", "append", "merge"];
  for (var aSection in sectionKeys) {
    var key = sectionKeys[aSection];
    var data = curImportInfo[key];
    if (data == null) continue;

    if (Array.isArray(data))
      data.forEach(function (item) {
        if (!IsConnectorRefreshable(item)) {
          canManualRefresh = false;
        }
      });
    else canManualRefresh = IsConnectorRefreshable(data);

    if (!canManualRefresh) return false;
  }
  return canManualRefresh;
};

export const getFeatureAccess = feature => {
  return Globals._accountInfo.GetAccessibleFeature(feature);
};

export const isSharepointExportEnabled = () => getFeatureAccess(enumFeaturesList.autoExport);

export const UPGRADE_BADGE_PROPS = {
  pin: "top-right",
  size: "large",
  color: "brand",
  border: false
};

export const getCurrentChartName = () => {
  const iWiz = StateLib.GetDoc()?.GetIWizData();
  const currentChartId = iWiz?.fBaseChartID;

  if (!currentChartId) return null;

  return getBaseChartName(currentChartId);
};

export const getBaseChartName = chartID => {
  return getBaseChartByID(chartID)?.name;
};

export const getBaseChartByID = chartID => {
  const wsMgr = StateLib.GetWSMgr();

  const chart = wsMgr
    ?.GetOrgInfo()
    ?.GetMasterChartList()
    .find(c => c.ID == chartID);

  if (chart) return chart;
  else return null;
};

export const loadChartMetadata = async chartID => {
  const { result } = await GetChartMetadata(chartID);
  return result;
};

export const buildUrlWithUTMParams = (baseURL, featureKey, params) => {
  const featureClicked = featureKey || "unknown_feature";

  if (!params) return `${baseURL}&feature_clicked=${featureClicked}`;

  //if params are passed replace existing utm params with new ones
  const url = new URL(baseURL);
  Object.keys(params).forEach(key => {
    url.searchParams.set(key, params[key]);
  });

  url.searchParams.set("feature_clicked", featureClicked);
  return url.toString();
};

export const buildUrlWithUtmTerm = (baseURL, utmTerm) => {
  const url = new URL(baseURL);
  url.searchParams.set("utm_term", utmTerm);
  return url.toString();
};
